import React, { forwardRef, useImperativeHandle } from 'react';
import { ComponentType } from '@philips-internal/oswald-ui-core';
import { AbortableCloseEvent, CloseEvent, OpenEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { useSharedState } from '@philips-internal/oswald-ui-engine';
import { createAbort, isFunction } from '@philips-internal/oswald-ui-utils';
import { HeadingType } from '../components/heading/heading-constants.js';
import { HeadingActions } from '../components/heading-actions/heading-actions.js';
import { HeadingContext, HeadingProvider } from '../components/heading-context/heading-context.js';
import { HeadingDynamicContent } from '../components/heading-dynamic-content/heading-dynamic-content.js';
import { HeadingSection } from '../components/heading-section/heading-section.js';
import { HeadingTitle } from '../components/heading-title/heading-title.js';
import { SubSectionProperties } from './subsection-types.js';
import './subsection.css';
const headerAcceptedTypes = [
    ComponentType.NAVIGATION,
];
class InternalSubSection extends HeadingSection {
    render() {
        const { collapsed, hideActions, inlineActionsDesktopLimit = SubSectionProperties.inlineActionsDesktopLimit.defaultValue, onCollapseButtonClick, showCollapseButton, uuid = SubSectionProperties.uuid.defaultValue, } = this.props;
        return (React.createElement("section", { "aria-labelledby": `${this.context?.getIdentifier()}-heading__title`, className: this.getClassName('c-subsection') },
            React.createElement("div", { className: this.getHeaderClassName('c-subsection__header'), onClick: (event) => this.onHeaderClick(event, showCollapseButton ? onCollapseButtonClick : undefined) },
                React.createElement("div", { className: "c-subsection__header-top" },
                    React.createElement(HeadingTitle, null),
                    React.createElement(HeadingActions, { acceptedTypes: headerAcceptedTypes, collapsed: collapsed, hideActions: hideActions, inlineLimit: inlineActionsDesktopLimit, onCollapseButtonClick: onCollapseButtonClick, showCollapseButton: showCollapseButton, uuid: uuid })),
                React.createElement("div", { className: "c-subsection__header-bottom" },
                    React.createElement(HeadingDynamicContent, null))),
            this.renderChildren('c-subsection__content', collapsed)));
    }
}
InternalSubSection.contextType = HeadingContext;
function SubSection(props, ref) {
    const [collapsed, setCollapsed] = useSharedState({
        initialValue: false,
        name: 'collapsed',
        props,
    });
    useImperativeHandle(ref, () => ({
        isCollapsed: () => collapsed,
        setCollapsed: (collapsed) => setCollapsed(collapsed),
    }), [collapsed, setCollapsed]);
    const onClose = () => {
        const { onClose } = props;
        if (!isFunction(onClose)) {
            return;
        }
        onClose(new CloseEvent());
    };
    const onOpen = () => {
        const { onOpen } = props;
        if (!isFunction(onOpen)) {
            return;
        }
        onOpen(new OpenEvent());
    };
    const onToggleCollapse = (collapsed) => {
        const handler = collapsed ? onClose : onOpen;
        handler();
    };
    const onCollapseButtonClick = () => {
        const { onBeforeClose } = props;
        const abort = createAbort();
        if (isFunction(onBeforeClose) && !collapsed) {
            onBeforeClose(new AbortableCloseEvent({ abort }));
        }
        void abort.promise.then(aborted => {
            if (aborted) {
                return;
            }
            setCollapsed(prevCollapsed => {
                onToggleCollapse(!prevCollapsed);
                return !prevCollapsed;
            });
        });
    };
    return (React.createElement(HeadingProvider, { type: HeadingType.SUBSECTION },
        React.createElement(InternalSubSection, { ...props, collapsed: collapsed, onCollapseButtonClick: onCollapseButtonClick })));
}
const SubSectionWithRef = forwardRef(SubSection);
SubSectionWithRef.displayName = SubSection.name;
export { SubSectionWithRef as SubSection, };
//# sourceMappingURL=subsection.js.map